/**
 * Google Analytics utility functions
 * Tracks user interactions and conversions
 */

// Check if gtag is available
const isAnalyticsAvailable = () => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Track a custom event in Google Analytics
 */
export const trackEvent = (
  eventName: string,
  parameters?: Record<string, string | number | boolean>
) => {
  if (!isAnalyticsAvailable()) {
    console.log('Analytics event (not sent):', eventName, parameters);
    return;
  }

  window.gtag('event', eventName, parameters);
  console.log('Analytics event sent:', eventName, parameters);
};

/**
 * Track page views (for SPA navigation)
 */
export const trackPageView = (path: string, title?: string) => {
  if (!isAnalyticsAvailable()) {
    console.log('Page view (not sent):', path, title);
    return;
  }

  window.gtag('config', 'G-K3WG1FWYTT', {
    page_path: path,
    page_title: title || document.title
  });
};

/**
 * Track form submissions
 */
export const trackFormSubmission = (formName: string, formData?: Record<string, string | number | boolean>) => {
  trackEvent('form_submit', {
    form_name: formName,
    form_destination: formName,
    ...formData
  });
};

/**
 * Track contact form conversion
 */
export const trackContactFormConversion = (serviceInterest?: string, formType = 'contact_form') => {
  // Track as conversion event. Call only after the submission succeeded,
  // so generate_lead counts real leads (one event per submission).
  trackEvent('generate_lead', {
    currency: 'USD',
    value: 100, // Estimated lead value
    form_type: formType,
    form_location: window.location.pathname,
    service_interest: serviceInterest || 'general'
  });
  
  // Also track as custom event for easier reporting
  trackEvent('contact_form_submission', {
    service_interest: serviceInterest || 'general',
    form_location: window.location.pathname
  });
};

/**
 * Track emergency contact request
 */
export const trackEmergencyContact = () => {
  trackEvent('emergency_contact', {
    urgency: 'high',
    action: 'form_submission'
  });
};

/**
 * Track outbound links
 */
export const trackOutboundLink = (url: string, linkText?: string) => {
  trackEvent('click', {
    event_category: 'outbound',
    event_label: url,
    link_text: linkText,
    transport_type: 'beacon'
  });
};

/**
 * Track phone clicks
 */
export const trackPhoneClick = (phoneNumber: string, location?: string) => {
  trackEvent('click_to_call', {
    phone_number: phoneNumber,
    click_location: location || 'unknown',
    device_type: /Mobile|Android|iPhone/i.test(navigator.userAgent) ? 'mobile' : 'desktop'
  });
};

/**
 * Track file downloads
 */
export const trackDownload = (fileName: string, fileType?: string) => {
  trackEvent('file_download', {
    file_name: fileName,
    file_type: fileType || 'unknown',
    link_url: fileName
  });
};

/**
 * Track search queries
 */
export const trackSearch = (searchTerm: string, resultsCount?: number) => {
  trackEvent('search', {
    search_term: searchTerm,
    results_count: resultsCount
  });
};

/**
 * Track video engagement
 */
export const trackVideoEngagement = (
  videoTitle: string,
  action: 'play' | 'pause' | 'complete',
  percentWatched?: number
) => {
  trackEvent('video_' + action, {
    video_title: videoTitle,
    video_percent: percentWatched
  });
};

/**
 * Track scroll depth
 */
export const trackScrollDepth = (percentage: number) => {
  trackEvent('scroll', {
    percent_scrolled: percentage,
    page_path: window.location.pathname
  });
};

/**
 * Track engagement time
 */
export const trackEngagementTime = (seconds: number) => {
  trackEvent('user_engagement', {
    engagement_time_msec: seconds * 1000,
    page_path: window.location.pathname
  });
};

/**
 * Track 404 errors
 */
export const track404Error = (attemptedPath: string) => {
  trackEvent('404_error', {
    page_location: attemptedPath,
    page_referrer: document.referrer
  });
};

/**
 * Track service page views
 */
export const trackServicePageView = (serviceName: string) => {
  trackEvent('view_item', {
    currency: 'USD',
    value: 0,
    items: [{
      item_name: serviceName,
      item_category: 'IT Services',
      item_category2: 'Service Page'
    }]
  });
};

/**
 * Track blog post engagement
 */
export const trackBlogEngagement = (
  postTitle: string,
  action: 'view' | 'read_complete' | 'share',
  category?: string
) => {
  trackEvent('blog_' + action, {
    post_title: postTitle,
    post_category: category,
    content_type: 'blog_post'
  });
};